import React, { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import {
  House, Storefront, ClipboardText, Wallet, Trophy, ChartLine,
  ShieldCheck, Users, GearSix, SignOut, Bell, MagnifyingGlass, Plus,
  GraduationCap, Sun, Moon, List, Lightning, Gift, Graph,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import NotificationBell from "./NotificationBell";
import { WorkMeshMark } from "./WorkMeshLogo";
import { useTheme } from "@/theme/ThemeContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["*"], testid: "nav-dashboard" },
  { to: "/marketplace", label: "Marketplace", icon: Storefront, roles: ["*"], testid: "nav-marketplace" },
  { to: "/my-tasks", label: "My Work", icon: ClipboardText, roles: ["*"], testid: "nav-my-tasks" },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, roles: ["*"], testid: "nav-approvals" },
  { to: "/wallet", label: "Wallet", icon: Wallet, roles: ["*"], testid: "nav-wallet" },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, roles: ["*"], testid: "nav-leaderboard" },
  { to: "/challenges", label: "Challenges", icon: Lightning, roles: ["*"], testid: "nav-challenges" },
  { to: "/rewards", label: "Rewards", icon: Gift, roles: ["*"], testid: "nav-rewards" },
  { to: "/skills", label: "Skill Market", icon: GraduationCap, roles: ["*"], testid: "nav-skills" },
  { to: "/capability", label: "Capability Graph", icon: Graph, roles: ["*"], testid: "nav-capability" },
  { to: "/analytics", label: "Analytics", icon: ChartLine, roles: ["ADMIN", "MANAGER"], testid: "nav-analytics" },
  { to: "/admin", label: "Admin", icon: GearSix, roles: ["ADMIN"], testid: "nav-admin" },
];

function SidebarContent({ user, onNavigate }) {
  return (
    <div className="h-full flex flex-col bg-surface/40" data-testid="sidebar-content">
      <div className="px-5 py-5 border-b border-border">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-link" onClick={onNavigate}>
          <WorkMeshMark size={28} />
          <div>
            <div className="text-base font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', 'Satoshi', sans-serif" }}>
              Work<span className="text-[#E1252A]">Mesh</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Internal Marketplace</div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {NAV.filter((n) => n.roles.includes("*") || n.roles.includes(user.role)).map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={onNavigate}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-foreground text-background" : "text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-900"
                }`
              }
            >
              <Icon size={16} weight="regular" />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500">Signed in as</div>
        <div className="px-2 text-sm truncate" data-testid="sidebar-user-name">{user.name}</div>
        <div className="px-2 text-xs text-zinc-500">{user.role}</div>
        <div className="px-2 pt-3 mt-3 border-t border-border text-[9px] uppercase tracking-[0.25em] text-zinc-400" data-testid="sidebar-brand-footer">
          A VeltrixAI product
        </div>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Close mobile drawer on route change
  React.useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  if (!user) return <>{children}</>;

  const initials = (user.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen flex bg-background text-foreground" data-testid="app-shell">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border" data-testid="sidebar">
        <SidebarContent user={user} onNavigate={() => {}} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent side="left" className="p-0 w-64 border-r border-border" data-testid="mobile-sidebar">
          <SidebarContent user={user} onNavigate={() => setMobileNavOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border flex items-center px-3 md:px-6 gap-2 md:gap-3 bg-background sticky top-0 z-10" data-testid="topbar">
          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-btn"
            className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-sm hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
          >
            <List size={20} />
          </button>

          {/* Mobile compact brand */}
          <Link to="/dashboard" className="md:hidden flex items-center gap-1.5" data-testid="mobile-brand">
            <WorkMeshMark size={22} />
            <span className="font-bold text-sm tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Work<span className="text-[#E1252A]">Mesh</span>
            </span>
          </Link>

          <div className="flex-1" />

          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/tasks/new")}
            className="rounded-sm px-2 md:px-3"
            data-testid="topbar-new-task-btn"
          >
            <Plus size={14} className="md:mr-1" />
            <span className="hidden sm:inline">New task</span>
          </Button>
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            data-testid="theme-toggle-btn"
            className="h-9 w-9 inline-flex items-center justify-center rounded-sm hover:bg-zinc-100 dark:hover:bg-zinc-900 transition-colors"
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-sm">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.avatar} />
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
              <DropdownMenuLabel>
                <div className="text-sm">{user.name}</div>
                <div className="text-xs text-zinc-500">{user.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(`/profile/${user.user_id}`)} data-testid="menu-profile">
                <Users size={14} className="mr-2" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/wallet")} data-testid="menu-wallet">
                <Wallet size={14} className="mr-2" /> Wallet
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                <SignOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
